import { createContext, useContext, useState } from "react";
import { jwtDecode } from "jwt-decode";
import PageLoader from "../components/PageLoader";
import { userRoles } from "../helpers/constant";

// Superadmins otherwise start with no facility filter, unlike the rest of the app
// which is already scoped to the user's facility. Derive their facility synchronously
// from the token so dashboards load scoped to it on the very first render — this avoids
// a null→value change that would make every facility-dependent fetch run twice.
// Only the facility_number is known here (the token carries it as facility_id); the
// dropdown resolves the display name from the facility list.
const getDefaultFacilityFilter = () => {
    try {
        const token = localStorage.getItem("token");
        if (!token) return null;
        const decoded = jwtDecode(token);
        if (
            decoded?.role?.toLowerCase() === userRoles.SUPERADMIN &&
            decoded.facility_id != null
        ) {
            return { facility_number: decoded.facility_id };
        }
        return null;
    } catch {
        return null;
    }
};

// Create context
const DashboardUIContext = createContext({

    // Facility List for filtering
    facilityList: [],
    setFacilityList: () => { },

    // Selected Facility Filter in sidebar Layout
    selectedFacilityFilter: null,
    setSelectedFacilityFilter: () => { },

    selectedStartDateFilter: null,
    setSelectedStartDateFilter: () => { },

    selectedEndDateFilter: null,
    setSelectedEndDateFilter: () => { },

    // Client app filter for the dashboard KPI cards: null = all clients,
    // "desktop_buddy" = NEMS Buddy desktop app, "platform" = web.
    selectedClientFilter: null,
    setSelectedClientFilter: () => { },

    totalActiveSessions: null,
    setTotalActiveSessions: () => { },

    fileGenSummary: { totalFiles: 0, totalImages: 0, maxImagesPerDay: 0 },
    setFileGenSummary: () => { },
});

// Provider
export const GlobalProvider = ({ children }) => {
    const [facilityList, setFacilityList] = useState([]);
    const [pageLoader, setPageLoader] = useState(false);
    const [selectedFacilityFilter, setSelectedFacilityFilter] = useState(getDefaultFacilityFilter);
    const [showForcedLogout, setShowForcedLogout] = useState(false);
    const [promptList, setPromptList] = useState([]);
    const [facilityUserList, setFacilityUserList] = useState([]);
    const [selectedStartDateFilter, setSelectedStartDateFilter] = useState(null);
    const [selectedEndDateFilter, setSelectedEndDateFilter] = useState(null);
    const [selectedClientFilter, setSelectedClientFilter] = useState(null);
    const [totalActiveSessions, setTotalActiveSessions] = useState(null);
    const [fileGenSummary, setFileGenSummary] = useState({ totalFiles: 0, totalImages: 0, maxImagesPerDay: 0 });
    const [llmModels, setLlmModels] = useState([]);

    return (
        <DashboardUIContext.Provider value={{
            facilityList,
            setFacilityList,
            pageLoader,
            setPageLoader,
            selectedFacilityFilter,
            setSelectedFacilityFilter,
            showForcedLogout,
            setShowForcedLogout,
            promptList,
            setPromptList,
            facilityUserList,
            setFacilityUserList,
            selectedStartDateFilter,
            setSelectedStartDateFilter,
            selectedEndDateFilter,
            setSelectedEndDateFilter,
            selectedClientFilter,
            setSelectedClientFilter,
            totalActiveSessions,
            setTotalActiveSessions,
            fileGenSummary,
            setFileGenSummary,
            llmModels,
            setLlmModels,
        }}>
            <PageLoader visible={pageLoader} />
            {children}
        </DashboardUIContext.Provider>
    );
};

// Custom hook
export const useDashboardUI = () => {
    const context = useContext(DashboardUIContext);
    if (!context) {
        throw new Error("useDashboardUI must be used within GlobalProvider");
    }
    return context;
};
